/* eslint-disable react/no-unescaped-entities */
/**
 * /pos/redeem — staff-facing screen on the venue's tablet / phone.
 *
 * Flow:
 *   1. Staff types or scans a customer code (uid).
 *   2. Page hits GET /balance to show the customer's pots
 *      (this business's credits + platform Hustle Dollars).
 *   3. Staff enters $ amount → POST /redeem → returns breakdown.
 *
 * QR scanning uses the native BarcodeDetector API where available
 * (Chrome/Edge mobile) and falls back to manual entry everywhere else.
 *
 * For an MVP demo the biz_id is taken from ?biz=… in the URL so the
 * tablet bookmark can be venue-specific:  /pos/redeem?biz=honey-salt
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Camera, X, CheckCircle2, Loader2, ScanLine, DollarSign } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-AU", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

export default function PosRedeemPage() {
  const [params] = useSearchParams();
  const bizId = params.get("biz") || "demo-honey-salt";

  const [step, setStep]   = useState("scan");          // scan | balance | confirm | done
  const [uid, setUid]     = useState("");
  const [balance, setBalance] = useState(null);
  const [amount, setAmount] = useState("");
  const [billRef, setBillRef] = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const lookup = useCallback(async (id) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.get(`/api/business/credits/balance`,
        { params: { biz_id: bizId, uid: id } });
      setBalance(r.data);
      setUid(id);
      setStep("balance");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [bizId]);

  const redeem = async () => {
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!cents || cents <= 0) { setError("Enter an amount"); return; }
    if (cents > balance.total_redeemable_cents) {
      setError("Amount exceeds customer's available credits");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/api/business/credits/redeem`, {
        biz_id: bizId, uid, amount_cents: cents,
        bill_ref: billRef || null,
      });
      setResult(r.data);
      setStep("done");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.root} data-testid="pos-redeem-root">
      <header style={styles.header}>
        <div>
          <div style={styles.brand}>HustleApp · POS</div>
          <div style={styles.biz}>Venue: <strong>{bizId}</strong></div>
        </div>
        <div style={styles.stepper}>
          {["Scan", "Balance", "Confirm", "Done"].map((label, i) => (
            <span key={i} style={{
              ...styles.stepDot,
              ...(["scan","balance","confirm","done"][i] === step ? styles.stepActive : {}),
            }}>
              {i + 1}. {label}
            </span>
          ))}
        </div>
      </header>

      {step === "scan" && (
        <ScanStep
          onLookup={lookup}
          busy={busy}
          error={error}
        />
      )}

      {step === "balance" && balance && (
        <div style={styles.card} data-testid="pos-balance-card">
          <div style={styles.uidLine}>
            <span style={styles.uidLabel}>Customer</span>
            <code style={styles.uidVal}>{uid}</code>
            <button
              onClick={() => { setStep("scan"); setBalance(null); setUid(""); }}
              style={styles.linkBtn}
              data-testid="pos-change-customer"
            >
              change
            </button>
          </div>

          <div style={styles.balanceGrid}>
            <div style={styles.balCell}>
              <div style={styles.balLabel}>{bizId} credits</div>
              <div style={styles.balValue} data-testid="pos-biz-credits">
                {fmt(balance.business_credits_cents)}
              </div>
              <div style={styles.balHelp}>Use first</div>
            </div>
            <div style={styles.balCell}>
              <div style={styles.balLabel}>Platform Hustle$</div>
              <div style={styles.balValue} data-testid="pos-platform-credits">
                {fmt(balance.platform_hd_cents)}
              </div>
              <div style={styles.balHelp}>Used after biz credits</div>
            </div>
            <div style={{ ...styles.balCell, ...styles.balCellTotal }}>
              <div style={styles.balLabel}>Total available</div>
              <div style={styles.balValueTotal} data-testid="pos-total">
                {fmt(balance.total_redeemable_cents)}
              </div>
            </div>
          </div>

          <div style={styles.amountForm}>
            <label style={styles.label}>Amount to redeem</label>
            <div style={styles.amountInput}>
              <DollarSign size={20} color="#64748b" />
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={styles.bigInput}
                autoFocus
                data-testid="pos-amount-input"
              />
            </div>
            <label style={styles.label}>Bill / order # (optional)</label>
            <input
              type="text"
              placeholder="e.g. #4729"
              value={billRef}
              onChange={(e) => setBillRef(e.target.value)}
              style={styles.refInput}
              data-testid="pos-bill-ref"
            />
            {error && <div style={styles.error}>{error}</div>}
            <button
              onClick={redeem}
              disabled={busy || !amount}
              style={styles.redeemBtn}
              data-testid="pos-redeem-button"
            >
              {busy ? <Loader2 size={18} className="spin" /> : <CheckCircle2 size={18} />}
              Redeem {amount ? `$${parseFloat(amount).toFixed(2)}` : ""}
            </button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div style={styles.card} data-testid="pos-done-card">
          <div style={styles.doneBadge}>
            <CheckCircle2 size={36} />
          </div>
          <h2 style={styles.doneTitle}>Redeemed {fmt(result.amount_cents)}</h2>
          <p style={styles.doneSub}>Charged off the customer's wallet</p>

          <div style={styles.doneBreakdown}>
            <div>
              <span>{bizId} credits used</span>
              <strong>{fmt(result.biz_credits_used_cents)}</strong>
            </div>
            <div>
              <span>Platform Hustle$ used</span>
              <strong>{fmt(result.platform_hd_used_cents)}</strong>
            </div>
            {result.reimbursement_id && (
              <div style={{ background: "#fef3c7", padding: "12px 14px",
                            borderRadius: 8, marginTop: 12, fontSize: 13,
                            color: "#92400e", lineHeight: 1.5 }}>
                Hustle owes you {fmt(result.platform_hd_used_cents)}. A
                payout request has been queued
                (ref: <code>{result.reimbursement_id?.slice(-8)}</code>) —
                paid within 14 days.
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setStep("scan"); setUid(""); setBalance(null);
              setAmount(""); setBillRef(""); setResult(null);
            }}
            style={styles.redeemBtn}
            data-testid="pos-next-customer"
          >
            Next customer
          </button>
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ScanStep({ onLookup, busy, error }) {
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCamera = async () => {
    if (typeof window === "undefined" || !("BarcodeDetector" in window)) {
      alert("QR scanning isn't supported in this browser. Use the manual entry below.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" } });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      setScanning(true);
       
      const detector = new BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes[0]) {
            stopCamera();
            onLookup(codes[0].rawValue);
            return;
          }
        } catch (_) { /* swallow */ }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (e) {
      alert(`Camera unavailable: ${e.message}`);
    }
  };

  useEffect(() => () => stopCamera(), []);

  return (
    <div style={styles.card} data-testid="pos-scan-card">
      <h2 style={{ ...styles.h2, marginTop: 0 }}>
        <ScanLine size={22} /> Scan customer code
      </h2>
      <p style={styles.sub}>
        Ask the customer to open the HustleApp wallet and present their QR.
        Tap below to scan, or enter their wallet ID manually.
      </p>

      {!scanning ? (
        <button onClick={startCamera} style={styles.scanBtn} data-testid="pos-scan-start">
          <Camera size={20} /> Scan QR
        </button>
      ) : (
        <div style={styles.scanWrap}>
          <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
          <button onClick={stopCamera} style={styles.scanCancel} data-testid="pos-scan-cancel">
            <X size={18} />
          </button>
        </div>
      )}

      <div style={styles.divider}>or enter manually</div>

      <form
        onSubmit={(e) => { e.preventDefault(); if (manual.trim()) onLookup(manual.trim()); }}
        style={{ display: "flex", gap: 8 }}
      >
        <input
          type="text"
          placeholder="customer wallet ID"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          style={styles.refInput}
          data-testid="pos-manual-uid"
        />
        <button
          type="submit"
          disabled={busy || !manual.trim()}
          style={styles.lookupBtn}
          data-testid="pos-lookup-button"
        >
          {busy ? <Loader2 size={16} className="spin" /> : "Look up"}
        </button>
      </form>

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

const styles = {
  root: { minHeight: "100vh", background: "#0f172a", color: "white",
          fontFamily: "Inter, system-ui, sans-serif", padding: "24px 16px" },
  header: { maxWidth: 560, margin: "0 auto 24px", display: "flex",
            justifyContent: "space-between", alignItems: "start", gap: 16,
            flexWrap: "wrap" },
  brand: { fontSize: 13, letterSpacing: "0.18em", color: "#94a3b8",
           textTransform: "uppercase", fontWeight: 700 },
  biz: { fontSize: 20, fontWeight: 700, marginTop: 4 },
  stepper: { display: "flex", gap: 6, flexWrap: "wrap" },
  stepDot: { padding: "4px 10px", borderRadius: 999, background: "#1e293b",
             color: "#64748b", fontSize: 11, fontWeight: 600 },
  stepActive: { background: "#fbbf24", color: "#0f172a" },
  card: { maxWidth: 560, margin: "0 auto", background: "white",
          color: "#0f172a", borderRadius: 16, padding: 28,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)" },
  h2: { display: "flex", alignItems: "center", gap: 8, fontSize: 20,
        fontWeight: 800, margin: "0 0 8px" },
  sub: { color: "#64748b", fontSize: 14, lineHeight: 1.55, margin: "0 0 24px" },
  scanBtn: { width: "100%", display: "inline-flex", alignItems: "center",
             justifyContent: "center", gap: 10, background: "#0f172a",
             color: "white", border: "none", padding: "16px",
             borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer" },
  scanWrap: { position: "relative", borderRadius: 12, overflow: "hidden",
              background: "black", aspectRatio: "1 / 1" },
  video: { width: "100%", height: "100%", objectFit: "cover" },
  scanCancel: { position: "absolute", top: 12, right: 12, background: "white",
                border: "none", borderRadius: "50%", width: 36, height: 36,
                cursor: "pointer", display: "inline-flex", alignItems: "center",
                justifyContent: "center" },
  divider: { textAlign: "center", color: "#94a3b8", fontSize: 12,
             textTransform: "uppercase", letterSpacing: "0.12em",
             margin: "24px 0 12px", fontWeight: 700 },
  refInput: { flex: 1, padding: "12px 14px", border: "1px solid #e2e8f0",
              borderRadius: 10, fontSize: 15, outline: "none",
              fontFamily: "inherit" },
  lookupBtn: { background: "#fbbf24", color: "#0f172a", border: "none",
               padding: "0 22px", borderRadius: 10, fontWeight: 700,
               cursor: "pointer" },
  uidLine: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  uidLabel: { fontSize: 11, textTransform: "uppercase",
              letterSpacing: "0.1em", color: "#64748b", fontWeight: 700 },
  uidVal: { background: "#f1f5f9", padding: "4px 10px", borderRadius: 6,
            fontSize: 13, color: "#334155" },
  linkBtn: { background: "none", border: "none", color: "#0ea5e9",
             cursor: "pointer", fontSize: 13, textDecoration: "underline" },
  balanceGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                 gap: 12, marginBottom: 28 },
  balCell: { background: "#f8fafc", border: "1px solid #e2e8f0",
             padding: 16, borderRadius: 12 },
  balCellTotal: { background: "#fef3c7", border: "1px solid #fde68a" },
  balLabel: { fontSize: 11, textTransform: "uppercase",
              letterSpacing: "0.1em", color: "#64748b", fontWeight: 700 },
  balValue: { fontSize: 22, fontWeight: 800, margin: "6px 0 2px",
              fontVariantNumeric: "tabular-nums" },
  balValueTotal: { fontSize: 26, fontWeight: 800, margin: "6px 0 2px",
                   color: "#92400e", fontVariantNumeric: "tabular-nums" },
  balHelp: { fontSize: 11, color: "#94a3b8" },
  amountForm: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: "#64748b",
           textTransform: "uppercase", letterSpacing: "0.08em",
           marginTop: 8 },
  amountInput: { display: "flex", alignItems: "center", gap: 8,
                 padding: "16px 18px", border: "2px solid #0f172a",
                 borderRadius: 14, background: "white" },
  bigInput: { flex: 1, border: "none", outline: "none", fontSize: 28,
              fontWeight: 800, fontFamily: "inherit",
              fontVariantNumeric: "tabular-nums" },
  redeemBtn: { marginTop: 16, width: "100%", display: "inline-flex",
               alignItems: "center", justifyContent: "center", gap: 8,
               background: "#0f172a", color: "white", border: "none",
               padding: "18px", borderRadius: 14, fontSize: 17,
               fontWeight: 700, cursor: "pointer" },
  error: { background: "#fee2e2", color: "#991b1b", padding: "10px 14px",
           borderRadius: 8, fontSize: 13, marginTop: 8 },
  doneBadge: { width: 64, height: 64, borderRadius: "50%",
               background: "#dcfce7", color: "#16a34a",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", marginBottom: 12 },
  doneTitle: { fontSize: 24, fontWeight: 800, margin: "0 0 6px",
               letterSpacing: "-0.02em" },
  doneSub: { color: "#64748b", margin: 0 },
  doneBreakdown: { background: "#f8fafc", border: "1px solid #e2e8f0",
                   borderRadius: 12, padding: 16, margin: "24px 0",
                   display: "flex", flexDirection: "column", gap: 8,
                   fontSize: 14 },
};
